import { BadgeV3, ButtonV2 } from '@cimplico/components';
import { ArrowRightIcon, CalendarDaysIcon } from '@heroicons/react/20/solid';
import { Subscription, SubscriptionDuration, SubscriptionStatus } from '../../../../types/firm';
import { formatDate } from '../../../../utilities/formatting';
import SectionLabel from '../../../../components/section-label/SectionLabel';
import plural from '../../../../utilities/plural';
import SettingsContainer from '../../../../components/settings-container/SettingsContainer';

type PlanProps = {
    subscription: Subscription;
    onManage: () => void;
};

function Plan({ subscription, onManage }: PlanProps): JSX.Element | null {
    if (!subscription) {
        return null;
    }

    const duration = subscription.duration === SubscriptionDuration.MONTHLY ? 'month' : 'year';

    return (
        <SettingsContainer>
            <SectionLabel
                title="Plan"
                description="Update your firm's subscription plan, such as its billing period and subscription tier."
            />
            <div className="grid gap-y-8 text-xs">
                <div className="grid gap-y-2">
                    <p className="text-gray-600 dark:text-gray-400 font-medium">Tier</p>
                    <section className="flex gap-2 items-center">
                        <p className="text-gray-900 dark:text-gray-200 font-bold capitalize">{subscription.tier.toLowerCase()}</p>
                        <SubscriptionStatusBadge status={subscription.status} />
                    </section>
                    <p className="text-gray-800 dark:text-gray-200 flex items-center gap-2">
                        AU{' '}
                        {new Intl.NumberFormat('en-AU', {
                            style: 'currency',
                            currency: 'AUD',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        }).format(subscription.total)}{' '}
                        / {duration}
                        <span className="text-gray-600 dark:text-gray-400">
                            (AU{' '}
                            {new Intl.NumberFormat('en-AU', {
                                style: 'currency',
                                currency: 'AUD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            }).format(subscription.pricePerEntity)}{' '}
                            x {subscription.activeEntities.allocation}{' '}
                            {plural(subscription.activeEntities.allocation, 'entity', 'entities')})
                        </span>
                    </p>
                </div>
                <div className="grid gap-y-2">
                    <p className="text-gray-600 dark:text-gray-400 font-medium">Plan Commitment</p>
                    <div className="flex gap-1 items-center text-gray-800 dark:text-gray-200">
                        <CalendarDaysIcon className="h-4 w-4" />
                        <label>{formatDate(subscription.annualBillingPeriodStart)}</label>
                        <ArrowRightIcon className="h-4 w-4" />
                        <label>{formatDate(subscription.annualBillingPeriodEnd)}</label>
                        <label className="text-gray-600 dark:text-gray-400">
                            (1 {subscription.duration === SubscriptionDuration.YEARLY ? 'year' : 'month'})
                        </label>
                    </div>
                </div>
                <div className="grid gap-y-2">
                    <p className="text-gray-600 dark:text-gray-400 font-medium">Billing Period</p>
                    <p className="text-gray-800 dark:text-gray-200">
                        <span className="capitalize">{subscription.duration.toLowerCase()}</span>{' '}
                        {subscription.nextInvoice && (
                            <span className="text-gray-600 dark:text-gray-400">
                                (Next invoice {formatDate(subscription.nextInvoice?.date)} for A
                                {new Intl.NumberFormat('en-AU', {
                                    style: 'currency',
                                    currency: 'AUD',
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                }).format(subscription.nextInvoice.amount)}
                                )
                            </span>
                        )}
                    </p>
                </div>
                <div>
                    <ButtonV2
                        size="sm"
                        type="secondary"
                        label="Manage"
                        onClick={onManage}
                        isDisabled={subscription.isBespokeAgreement}
                        disabledHint={'This subscription is a bespoke agreement and cannot be updated. Please contact support.'}
                    />
                </div>
            </div>
        </SettingsContainer>
    );
}

function SubscriptionStatusBadge({ status }: { status?: SubscriptionStatus }): JSX.Element | null {
    if (status === SubscriptionStatus.TRIALING) {
        return <BadgeV3 size="small" label="Trialing" color="indigo" rounded={false} />;
    }
    if (status === SubscriptionStatus.ACTIVE) {
        return <BadgeV3 size="small" label="Active" color="green" rounded={false} />;
    }
    if (status === SubscriptionStatus.PAST_DUE) {
        return <BadgeV3 size="small" label="Past Due" color="yellow" rounded={false} />;
    }
    if (status === SubscriptionStatus.CANCELED) {
        return <BadgeV3 size="small" label="Cancelled" color="red" rounded={false} />;
    }
    if (status === SubscriptionStatus.PAUSED) {
        return <BadgeV3 size="small" label="Paused" color="gray" rounded={false} />;
    }
    return null;
}

export default Plan;
